import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where, 
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Create new affiliate
export const createAffiliate = async (affiliateData) => {
  try {
    const affiliate = {
      ...affiliateData,
      code: affiliateData.code.toUpperCase(),
      discountPercent: 20,
      commissionPercent: 50, // 50% of discounted amount
      isActive: true,
      totalEarnings: 0,
      totalReferrals: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'affiliates'), affiliate);
    return { id: docRef.id, ...affiliate };
  } catch (error) {
    console.error('Error creating affiliate:', error);
    throw error;
  }
};

// Get affiliate by code
export const getAffiliateByCode = async (code) => {
  try {
    const q = query(
      collection(db, 'affiliates'),
      where('code', '==', code.toUpperCase()),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }
    
    const affiliateDoc = querySnapshot.docs[0];
    return { id: affiliateDoc.id, ...affiliateDoc.data() };
  } catch (error) {
    console.error('Error getting affiliate by code:', error);
    throw error;
  }
};

// Get affiliate by ID
export const getAffiliateById = async (affiliateId) => {
  try {
    const affiliateDoc = await getDoc(doc(db, 'affiliates', affiliateId));
    if (!affiliateDoc.exists()) {
      return null;
    }
    
    return { id: affiliateDoc.id, ...affiliateDoc.data() };
  } catch (error) {
    console.error('Error getting affiliate by ID:', error);
    throw error;
  }
};

// Get all affiliates (admin)
export const getAllAffiliates = async () => {
  try {
    const q = query(
      collection(db, 'affiliates'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all affiliates:', error);
    throw error;
  }
};

// Track affiliate commission
export const trackAffiliateCommission = async (affiliateId, paymentData) => {
  try {
    const commissionAmount = paymentData.finalAmount * 0.50; // 50% of discounted amount
    
    // Create commission record
    const commission = {
      affiliateId,
      paymentId: paymentData.id,
      userId: paymentData.userId,
      originalAmount: paymentData.originalAmount,
      discountAmount: paymentData.discountAmount,
      finalAmount: paymentData.finalAmount,
      commissionAmount,
      status: 'pending',
      createdAt: new Date()
    };
    
    await addDoc(collection(db, 'commissions'), commission);
    
    // Update affiliate stats
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    const affiliateDoc = await getDoc(affiliateRef);
    
    if (affiliateDoc.exists()) {
      const currentData = affiliateDoc.data();
      await updateDoc(affiliateRef, {
        totalEarnings: (currentData.totalEarnings || 0) + commissionAmount,
        totalReferrals: (currentData.totalReferrals || 0) + 1,
        updatedAt: new Date()
      });
    }
    
    return commission;
  } catch (error) {
    console.error('Error tracking affiliate commission:', error);
    throw error;
  }
};

// Get affiliate commissions
export const getAffiliateCommissions = async (affiliateId) => {
  try {
    const q = query(
      collection(db, 'commissions'),
      where('affiliateId', '==', affiliateId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort in JavaScript
  } catch (error) {
    console.error('Error getting affiliate commissions:', error);
    throw error;
  }
};

// Update affiliate status
export const updateAffiliateStatus = async (affiliateId, isActive) => {
  try {
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    await updateDoc(affiliateRef, {
      isActive,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating affiliate status:', error);
    throw error;
  }
};

// Mark commission as paid
export const markCommissionAsPaid = async (commissionId) => {
  try {
    const commissionRef = doc(db, 'commissions', commissionId);
    await updateDoc(commissionRef, {
      status: 'paid',
      paidAt: new Date()
    });
  } catch (error) {
    console.error('Error marking commission as paid:', error);
    throw error;
  }
};
